import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faCheck, faCircle} from '@fortawesome/free-solid-svg-icons'
import moment from 'moment-timezone'
import _ from 'lodash'
// Layouts
import Section from '../layout/section'
// Utils
import slugify from '../../utils/slugify'
// Styles
import styles from './submission-history.module.scss'

interface Submission {
  _id: string
  category_name: string
  sub_category_name: string
  photo_name: string | null
  submitted_item: string
  submitted_time: string
}

interface SubmissionHistoryProps {
  currentVoter: {
    submissions?: Partial<Submission>[]
  } | null
}

const SubmissionHistory = ({currentVoter}: SubmissionHistoryProps) => {
  const submissions = (currentVoter?.submissions || []).filter((s): s is Submission => Boolean(s?._id))

  const totalCounts = _.reduce(submissions, (acc: Record<string, number>, submission) => {
    const key = `${submission.sub_category_name}|${submission.submitted_item}`
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const groupByFormattedTime = (submission: Submission) => moment(submission.submitted_time).format('YYYY-MM-DD')

  const timeGroupedSubmissions = _(submissions)
    .groupBy(groupByFormattedTime)
    .map((timeGroup, submittedTime) => ({
      submittedTime,
      groups: _(timeGroup)
        .groupBy('category_name')
        .map((groups, groupName) => ({
          key: `${slugify(groupName)}-${timeGroup}`,
          groupName,
          categories: _(groups)
            .groupBy('sub_category_name')
            .map((questions, categoryName) => ({
              key: `${slugify(categoryName)}-${groupName}`,
              categoryName,
              submissions: _(questions)
                .groupBy(s => `${s.photo_name}|${s.submitted_item}`)
                .map((_photoGroup, key) => {
                  const [photoName, submittedItem] = key.split('|')
                  return {
                    key: photoName,
                    photoName,
                    submittedItem,
                    totalSubmissions: totalCounts[`${categoryName}|${submittedItem}`],
                  }
                })
                .value(),
            }))
            .value(),
        }))
        .value(),
    }))
    .orderBy(['submittedTime'], ['desc'])
    .value()

  const groupGroupedSubmissions = _(submissions)
    .groupBy('category_name')
    .map((groupSubmissions, groupName) => ({
      key: groupName,
      groupName,
      submissions: _.flatMap(groupSubmissions, submission => ({
        key: `${submission.sub_category_name}-${submission.submitted_item}`,
        questionName: submission.sub_category_name,
        photoName: submission.photo_name,
        submittedItem: submission.submitted_item,
        submittedTime: submission.submitted_time,
      })),
    }))
    .orderBy(['submittedTime'], ['desc'])
    .value()

  if (timeGroupedSubmissions.length <= 0 && groupGroupedSubmissions.length <= 0) {
    return (
      <div className={styles['__no-items']}>
        <h3>You haven't submitted any entries for this ballot</h3>
        <h4>Please return back once you have</h4>
      </div>
    )
  }

  return (
    <Section
      styleModifiers={[
        '--is-div',
        '--is-display-block',
        '--is-centered-horizontally',
      ]}
    >
      <div className={styles['submission-history']}>
        <ul className={styles['__groups-list']}>
          {_.map(groupGroupedSubmissions, group => {
            return (
              <li
                key={group.key}
                className={styles['__groups-list-item']}
              >
                {groupGroupedSubmissions.length > 1 && <div className={styles['__list-header']}>{group.groupName}</div>}
                <ul className={styles['__submissions-list']}>
                  { _.map(_.orderBy(group.submissions, ['submittedTime'], ['desc']), submission => {
                    return (
                      <li
                        key={submission.key}
                        className={styles['__submissions-list-item']}
                      >
                        <div className={styles['__link-contents']}>
                          <div className={styles['__icon-container']}>
                            <span className='fa-layers fa-3x'>
                              <FontAwesomeIcon icon={faCircle} color='#47a87a' />
                              <FontAwesomeIcon icon={faCheck} inverse transform='shrink-6' />
                            </span>
                          </div>
                          <div className={styles['__text-container']}>
                            <div className={styles['__question-name']}>{submission.questionName}</div>
                            <div className={styles['__submission-name']}>{submission?.photoName}</div>
                          </div>
                        </div>
                      </li>
                    )
                  }) }
                </ul>
              </li>
            )
          })}
        </ul>
      </div>
    </Section>
  )
}

export default SubmissionHistory
